import Minilog from 'minilog';

import {createMergeAction} from './base';
import {addErrorMessage} from './feedback';
import {CalibrationUnitResource,  CalibrationStatusResource} from "./services";
import {getCabinetDataForPremise} from "./cabinets";
import {getStackTrace} from "./core/exceptions";
import * as Locale from './locale';

let logger = Minilog('frontend.models.calibration');

export var CalibrationUnitAPI: CalibrationUnitResource = new CalibrationUnitResource();
export var CalibrationStatusAPI: CalibrationStatusResource = new CalibrationStatusResource();

/**
 * Enumeration of all the calibration status types
 */
export class CalibrationStatusTypes {
    static STATUS_IN_PROGRESS = 'IN_PROGRESS';
    static STATUS_CANCELLED = 'CANCELLED';
    static STATUS_ERROR = 'ERROR';
    static STATUS_SUCCESS = 'SUCCESS';
    static STATUS_FAIL = 'FAIL';
}

export interface CalibrationStatusParameters {
    unit_id: string;
    calibration_unit_id: string;
    status_type: any;
    metadata: any;
}

/**
 * Merge: calibration Units for Calibration page
 */
function setCalibrationUnits(unitsList: any) {
    return createMergeAction(
        'calibration_unit_list',
        {data: {calibration: {units: unitsList}}},
        {array_replace: true}
    );
}

/**
 * Merge: disable loading progress for calibration page
 */
export function disableCalibrationLoad() {
    return createMergeAction(
        'calibration_loaded',
        {ui: {calibration: {loading: false}}}
    );
}

/**
 * Merge: enable loading progress for calibration page
 */
function enableCalibrationLoad() {
    return createMergeAction(
        'calibration_loading',
        {ui: {calibration: {loading: true}}}
    );
}

/**
 * Thunk: gets list of calibration statuses for the given premises units.
 *
 * @param premises
 * @returns {(dispatch:any)=>Promise<T>|Promise<R>|Promise<R|U>}
 */
export function getCalibrationStatusByPremises(premises: Number) : Function {
    return function (dispatch) {
        dispatch(enableCalibrationLoad());
        return dispatch(getCabinetDataForPremise(premises)).then((response) => {
            let unitList = response.data.map((u) => {
                return u.unit_id;
            });
            return dispatch(getCalibrationStatusList(unitList));
        }).catch((error) => {
            dispatch(addErrorMessage(Locale.TEXT_ERROR_CALIBRATION_START));
            dispatch(disableCalibrationLoad());
            return Promise.reject(error);
        });
    };
}

/**
 * Thunk: list all calibration units available
 */
export function getCalibrationUnits() {
    return function (dispatch) {
        dispatch(enableCalibrationLoad());
        return CalibrationUnitAPI.listAll().then(function(response) {
            let units = response.data;
            dispatch(setCalibrationUnits(units));
            dispatch(disableCalibrationLoad());
            return Promise.resolve(units);
        }).catch(function(error) {
            logger.error(`Failed to load calibration units: ${error}`);
            logger.debug(getStackTrace(error));
            dispatch(addErrorMessage(Locale.TEXT_ERROR_CALIBRATION_UNITS));
            dispatch(disableCalibrationLoad());
            return Promise.reject(error);
        });
    };
}

/**
 * Thunk: resets all in-progress calibrations
 */
export function resetCalibrationProcess() {
    return function (dispatch) {
        dispatch(enableCalibrationLoad());
        // currently no response.data from stop
        return CalibrationStatusAPI.stop().then(function(response) {
            dispatch(disableCalibrationLoad());
            logger.info('Cancelled calibration process');
            return Promise.resolve(response);
        }).catch(function(error) {
            logger.error(`Failed to cancel calibration process : ${error}`);
            logger.debug(getStackTrace(error));
            dispatch(addErrorMessage(Locale.TEXT_ERROR_CALIBRATION_STATUS));
            dispatch(disableCalibrationLoad());
            return Promise.reject(error);
        });
    };
}

/**
 * Thunk: creates a new calibration status for a given unit
 */
export function createCalibrationStatus(payload: any) {
    return function (dispatch) {
        dispatch(enableCalibrationLoad());
        return CalibrationStatusAPI.create(payload).then(function(response) {
            dispatch(disableCalibrationLoad());
            return Promise.resolve(response.data);
        }).catch(function(error) {
            logger.error(`Failed to create calibration status for ${payload.unit_id}: ${error}`);
            logger.debug(getStackTrace(error));
            dispatch(addErrorMessage(Locale.TEXT_ERROR_CALIBRATION_START));
            dispatch(disableCalibrationLoad());
            return Promise.reject(error);
        });
    };
}

/**
 * Thunk: retrieve the latest calibration statuses, optionally filtered by units
 */
export function getCalibrationStatusList(unitIds?: string[]) {
    return function (dispatch) {
        dispatch(enableCalibrationLoad());
        let params: any = {latest: true};
        if (unitIds) {
            params.unit_id = unitIds;
        }
        return CalibrationStatusAPI.listAll(params).then(function(response) {
            dispatch(disableCalibrationLoad());
            return Promise.resolve(response.data);
        }).catch(function(error) {
            logger.error(`Failed to get calibration status: ${error}`);
            logger.debug(getStackTrace(error));
            dispatch(addErrorMessage(Locale.TEXT_ERROR_CALIBRATION_START));
            dispatch(disableCalibrationLoad());
            return Promise.reject(error);
        });
    };
}
